import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../use-api";

interface HealArgusInstanceInput {
  instanceId: string;
}

/**
 * Invokes `POST /client/argus/instances/{id}/heal` — best-effort reconnect
 * and revive of a drifted Argus sandbox. See `heal_instance` in
 * `be/api/argus/client.py` for semantics.
 *
 * Used by two call sites:
 *   1. `use-argus-connection.ts` auto-fires this once per drift episode when
 *      SSE errors out on an instance the DB still believes is `active`.
 *   2. The `/argus` header renders a manual "Reconnect" button that calls
 *      the same mutation as a last-resort fallback.
 *
 * On success, invalidates the instance detail query so the UI re-reads
 * the refreshed host_url / access_token and SSE reconnects cleanly.
 */
export function useHealArgusInstance() {
  const queryClient = useQueryClient();
  const { callApi } = useApi<void>();

  return useMutation({
    mutationFn: async (input: HealArgusInstanceInput) => {
      await callApi(`/client/argus/instances/${input.instanceId}/heal`, {
        method: "POST",
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].startsWith("/client/argus/instances"),
      });
      queryClient.invalidateQueries({
        queryKey: [`/client/argus/instances/${variables.instanceId}`],
      });
    },
  });
}
